import { MediaType } from '@repo/rpc-types-creativefabrica-modality-v1'
import type { FieldValue } from '@repo/rpc-types-creativefabrica-preset-v1'
import { Tool } from '../gen/service/creativefabrica/studioagent/v1/types_pb'
import type {
  GenerationPlan,
  ToolJson,
} from '../gen/service/creativefabrica/studioagent/v1/types_pb'
import type { ChatMessage } from '../types'
import { TOKEN, tokenLabel } from './tokens'

const STRUCTURAL_ROLES = new Set(['firstFrame', 'lastFrame', 'sourceImage', 'sourceVideo'])

export function toolFromJson(tool: ToolJson | undefined): Tool {
  switch (tool) {
    case 'TOOL_LIST_MODELS': {
      return Tool.LIST_MODELS
    }
    case 'TOOL_GET_MODEL_DETAILS': {
      return Tool.GET_MODEL_DETAILS
    }
    case 'TOOL_GENERATE': {
      return Tool.GENERATE
    }
    case 'TOOL_GET_GENERATION_STATUS': {
      return Tool.GET_GENERATION_STATUS
    }
    case 'TOOL_LIST_REFS': {
      return Tool.LIST_REFS
    }
    case 'TOOL_INSPECT_REF': {
      return Tool.INSPECT_REF
    }
    case 'TOOL_ESTIMATE_COST': {
      return Tool.ESTIMATE_COST
    }
    case 'TOOL_LIST_VOICES': {
      return Tool.LIST_VOICES
    }
    case 'TOOL_CHOOSE_MODEL': {
      return Tool.CHOOSE_MODEL
    }
    case 'TOOL_PROGRESS_UPDATE': {
      return Tool.PROGRESS_UPDATE
    }
    default: {
      return Tool.UNSPECIFIED
    }
  }
}

export function toolLabel(tool: Tool, summary: string) {
  const line = summary.trim()
  if (line) {
    return line
  }
  switch (tool) {
    case Tool.LIST_MODELS: {
      return 'Looking up models'
    }
    case Tool.GET_MODEL_DETAILS: {
      return 'Checking a model'
    }
    case Tool.GENERATE: {
      return 'Preparing a generation'
    }
    case Tool.GET_GENERATION_STATUS: {
      return 'Checking a generation'
    }
    case Tool.LIST_REFS: {
      return 'Looking up assets'
    }
    case Tool.INSPECT_REF: {
      return 'Inspecting an asset'
    }
    case Tool.ESTIMATE_COST: {
      return 'Estimating cost'
    }
    case Tool.LIST_VOICES: {
      return 'Looking up voices'
    }
    case Tool.CHOOSE_MODEL: {
      return 'Choosing a model'
    }
    case Tool.PROGRESS_UPDATE: {
      // splitProgressUpdates keeps an update hidden until its text arrives.
      return ''
    }
    default: {
      return 'Working'
    }
  }
}

function fieldText(value: FieldValue) {
  switch (value.value.case) {
    case 'stringValue':
    case 'integerValue':
    case 'floatValue': {
      return String(value.value.value)
    }
    case 'booleanValue': {
      return value.value.value ? 'true' : 'false'
    }
    default: {
      return ''
    }
  }
}

export function textOptions(options: Readonly<Record<string, FieldValue>> | undefined) {
  return Object.entries(options ?? {}).flatMap(([name, value]) => {
    const text = fieldText(value)
    return text ? [{ name, value: text }] : []
  })
}

function outputTitle(mediaType: MediaType) {
  switch (mediaType) {
    case MediaType.VIDEO: {
      return 'Approve video generation'
    }
    case MediaType.AUDIO: {
      return 'Approve audio generation'
    }
    default: {
      return 'Approve image generation'
    }
  }
}

export function planFrom(
  mediaType: MediaType,
  plan: GenerationPlan | undefined,
): NonNullable<ChatMessage['approval']>['plan'] {
  if (!plan) {
    return undefined
  }
  return {
    title: outputTitle(mediaType),
    model: plan.model,
    options: textOptions(plan.options),
    inputs: plan.inputs.flatMap(input => {
      const ref = input.inputRef?.ref
      if (!ref) {
        return []
      }
      return [
        {
          ref,
          label: input.alias ? tokenLabel(`{{${input.alias}}}`) : input.role || tokenLabel(ref),
          previewUrl: input.inputRef?.previewUrl || undefined,
          caption: input.usageExplanation,
          inPrompt: !STRUCTURAL_ROLES.has(input.role) && Boolean(input.alias),
        },
      ]
    }),
  }
}

export function promptTokens(prompt: string): string[] {
  const matches = prompt.match(TOKEN)
  return matches ? Array.from(matches) : []
}

export function promptPieces(text: string): Array<{ kind: 'text' | 'token'; value: string }> {
  const pieces: Array<{ kind: 'text' | 'token'; value: string }> = []
  const pattern = new RegExp(TOKEN.source, 'g')
  let cursor = 0
  for (const match of text.matchAll(pattern)) {
    const token = match[0]
    const start = match.index
    if (!token || start === undefined) {
      continue
    }
    if (start > cursor) {
      pieces.push({ kind: 'text', value: text.slice(cursor, start) })
    }
    pieces.push({ kind: 'token', value: token })
    cursor = start + token.length
  }
  if (cursor < text.length) {
    pieces.push({ kind: 'text', value: text.slice(cursor) })
  }
  return pieces
}

export function hasPromptToken(prompt: string, token: string): boolean {
  return promptTokens(prompt).some(existing => existing === token)
}
